import { bisectLeft } from 'd3-array'
import type { ScaleTime } from 'd3-scale'
import type { MouseEvent } from 'react'

type GraphBackgroundProps = {
  width: number
  height: number
  datetimes: Date[]
  timeScale: ScaleTime<number, number>
  /** Called with (index, relativeX, relativeY, viewportX, viewportY) */
  onMouseMove: (index: number, x: number, y: number, clientX: number, clientY: number) => void
  onMouseOut: () => void
  /** Optional click handler called with (hourIndex, relativeX, relativeY) */
  onClick?: (index: number, x: number, y: number) => void
}

/**
 * Invisible interaction layer for hover and keyboard detection.
 * Uses D3 bisectLeft for efficient index lookup.
 */
function GraphBackground({
  width,
  height,
  datetimes,
  timeScale,
  onMouseMove,
  onMouseOut,
  onClick,
}: Readonly<GraphBackgroundProps>) {
  const resolveIndex = (event: MouseEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const datetime = timeScale.invert(x)
    const insertionIndex = bisectLeft(datetimes, datetime)
    let index = insertionIndex > 0 ? insertionIndex - 1 : 0
    if (index >= datetimes.length) index = datetimes.length - 1
    return { index, x, y }
  }

  const handleClick = (event: MouseEvent<SVGRectElement>) => {
    if (!onClick) return
    const { index, x, y } = resolveIndex(event)
    onClick(index, x, y)
  }

  const handleMouseMove = (event: MouseEvent<SVGRectElement>) => {
    const { index, x, y } = resolveIndex(event)
    onMouseMove(index, x, y, event.clientX, event.clientY)
  }

  return (
    <rect
      className="graph-background"
      fill="transparent"
      height={height}
      style={{ cursor: onClick ? 'pointer' : 'crosshair' }}
      width={width}
      onClick={handleClick}
      onMouseLeave={onMouseOut}
      onMouseMove={handleMouseMove}
    />
  )
}

export default GraphBackground
